import React from 'react';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import { routes } from '../../../../../config/routes';
import { SEGMENT_EVENTS } from '../../../../../segment';
import withSegmentHook from '../../../../../segment/hooks/withSegmentHook';
import withInvoiceItemsEditor from '../withInvoiceItemsEditor';
import Invoice from './Invoice';

// Type definitions
interface PaymentDetails {
  isInvoice?: boolean;
  dateDue?: string | null;
  dateServiceStart?: string | null;
  dateServiceEnd?: string | null;
  total?: number;
  invoiceItemList?: InvoiceItem[];
  invoiceSummary?: InvoiceSummary;
}

interface InvoiceItem {
  category: string;
  item: string;
  amount: number;
  discounts: Discount[];
  itemOptions?: any[];
}

interface Discount {
  amount: number;
  amountType: string;
  discountType: string;
}

interface InvoiceSummary {
  total: number;
  items: Array<{
    label: string;
    amount: number;
    discounts: Array<{
      label: string;
      amount: number;
      amountType: string;
    }>;
  }>;
}

interface ComponentState {
  dateDue: string | null;
  dateServiceStart: string | null;
  dateServiceEnd: string | null;
}

// Props from withInvoiceItemsEditor HOC
interface InvoiceItemsEditorProps {
  validateFields: (nextClicked?: boolean) => boolean;
  getInvoiceSummary: () => InvoiceSummary;
  updatePaymentDetails: (details: any) => void;
  invoiceItemList: InvoiceItem[];
  total: number;
}

// Props from withSegmentHook HOC
interface SegmentHookProps {
  segmentTrack: (event: string, data?: any) => void;
}

// Props from withNavigate HOC
interface NavigateProps {
  navigate: NavigateFunction;
}

interface OwnProps {
  paymentDetails?: PaymentDetails;
  firstInvoiceCreated?: boolean;
  fireSubmit?: boolean;
}

type Props = OwnProps & InvoiceItemsEditorProps & SegmentHookProps & NavigateProps;

class InvoiceContainer extends React.Component<Props, ComponentState> {
  static defaultProps = {
    paymentDetails: undefined,
    firstInvoiceCreated: false,
    fireSubmit: false,
  };

  state: ComponentState = {
    dateDue: null,
    dateServiceStart: null,
    dateServiceEnd: null,
  };

  componentDidMount() {
    if (this.props.paymentDetails && this.props.paymentDetails.isInvoice) {
      this.setState({
        dateDue: this.props.paymentDetails.dateDue || null,
        dateServiceStart: this.props.paymentDetails.dateServiceStart || null,
        dateServiceEnd: this.props.paymentDetails.dateServiceEnd || null,
      });
    }
  }

  handleChange = (_e: React.SyntheticEvent, { name, value }: { name: string; value: string | null }) => {
    // Enforce date selection
    if (name === 'dateDue') {
      this.setState({
        dateDue: value,
        dateServiceStart: null,
        dateServiceEnd: null,
      });
    } else if (name === 'dateServiceStart') {
      this.setState({
        dateServiceStart: value,
        dateServiceEnd: null,
      });
    } else {
      if (name in this.state) {
        this.setState({ [name]: value } as any);
      }
    }
  };

  saveInvoiceData = () => {
    const { invoiceItemList, total } = this.props;

    if (this.props.validateFields(true)) {
      const invoiceSummary = this.props.getInvoiceSummary();
      this.props.updatePaymentDetails({
        ...this.state,
        invoiceItemList,
        total,
        invoiceSummary,
        isInvoice: true,
      });
      if (!this.props.firstInvoiceCreated) {
        this.props.segmentTrack(SEGMENT_EVENTS.firstInvoiceDetailsSuccess, invoiceSummary);
      }
      this.props.navigate(routes.billing.select);
    }
  };

  componentDidUpdate(prevProps: Props, _prevState: ComponentState, _snapshot: any) {
    // trigger submit from a parent form
    if (this.props.fireSubmit === true && prevProps.fireSubmit !== true) {
      this.saveInvoiceData();
    }
  }

  render() {
    return <Invoice onChange={this.handleChange} {...this.state} {...this.props} />;
  }
}

// HOC wrapper to inject navigation hook
function withNavigate<T extends object>(Component: React.ComponentType<T & NavigateProps>) {
  return function WrappedComponent(props: T) {
    const navigate = useNavigate();
    return <Component {...props} navigate={navigate} />;
  };
}

export default withSegmentHook(withInvoiceItemsEditor(withNavigate(InvoiceContainer)));
